import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import {
  GROUP_AGGREGATION_RESOURCE_TYPES,
  POLICY_PREFIX_OVERRIDES,
  TAG_AGGREGATION_RESOURCE_TYPES,
  WILD_CARD_ONLY_RESOURCE_TYPES,
} from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.constants';
import {
  TPolicyInputSchema,
  TCommonScope,
  TCommonScopeMode,
} from '@/shared/features/rbac/components/create-policy/policy-editor/policy-editor.types';
import { TPolicy } from '@/shared/features/rbac/components/policies/policies.types';
import { TResourceAggregations } from '@/shared/features/rbac/context/rbac-config-context.types';
import { WILD_CARD } from '@/shared/features/rbac/hooks/use-wild-card-option/use-wild-card-option';

/**
 * Resolve the resource type for a policy from its action/resource. Looks the
 * action up in the configured policies first — Backoffice namespaces actions
 * per resource (`backofficeUser:`, `backofficeTenant:`, …) whose prefix doesn't
 * map onto a `ResourceTypeEnum` value, so the config lookup is what identifies
 * the resource — then falls back to the action/resource prefix for actions not
 * present in the config (e.g. hand-typed JSON).
 */
export const getResourceTypeFromActions = (
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
  action?: string,
  resource?: string,
): ResourceTypeEnum => {
  const matchedEntry = action
    ? Object.entries(configuredPolicies).find(([, actions]) =>
        actions.includes(action),
      )
    : undefined;

  if (matchedEntry) {
    return matchedEntry[0] as ResourceTypeEnum;
  }

  const resourceTypes = [action?.split(':')[0], resource?.split(':')[0]];
  const validType = resourceTypes.find((type) => type && type !== '*');

  if (validType?.toLowerCase() === 'resourcegroup') {
    return ResourceTypeEnum.Group;
  }

  if (validType?.toLowerCase() === 'auditlog') {
    return ResourceTypeEnum.AuditLog;
  }

  return (validType?.toUpperCase() as ResourceTypeEnum) || defaultResourceType;
};

/**
 * Whether the JSON policy editor has no usable content — empty/whitespace text
 * or an empty array. Invalid-but-non-empty JSON counts as having content so the
 * user can still copy/download what they typed to fix it elsewhere.
 */
export const isPolicyJsonEmpty = (jsonPolicy?: string): boolean => {
  if (!jsonPolicy?.trim()) {
    return true;
  }

  try {
    const parsed = JSON.parse(jsonPolicy);
    return Array.isArray(parsed) && parsed.length === 0;
  } catch {
    return false;
  }
};

/**
 * Resource strings encode their aggregation: groups contain `:group:` (or the
 * `resourceGroup:` prefix), tags contain `:tag:` (or the `tag:` prefix), and
 * everything else (`<type>:id:<id>`, `<type>:*`) is a direct id resource. The
 * id/group/tag selectors all write to the same `resource` field, so these
 * classifiers let each one display only its own selections.
 */
export const isGroupResource = (resource: string): boolean =>
  resource.includes(':group:') || resource.startsWith('resourceGroup:');

export const isTagResource = (resource: string): boolean =>
  resource.includes(':tag:') || resource.startsWith('tag:');

export const isIdResource = (resource: string): boolean =>
  !isGroupResource(resource) && !isTagResource(resource);

/**
 * What the id selector displays: direct ids, plus values whose aggregation the
 * resource type no longer offers. Without the latter, a legacy `<type>:tag:x` on
 * a type that lost its tag tab would stay in the policy while invisible here.
 */
export const createIdResourceFilter =
  (resourceType: string) =>
  (resource: string): boolean => {
    if (isTagResource(resource)) {
      return !supportsTagAggregation(resourceType);
    }

    if (isGroupResource(resource)) {
      return !supportsGroupAggregation(resourceType);
    }

    return true;
  };

/**
 * Removes the UI-only fields (`resourceType`, `usesCommonScope`) from each
 * policy so the value sent to the JSON editor and the backend only contains
 * real policy fields.
 */
export const stripPolicyUiFields = <
  T extends { resourceType?: unknown; usesCommonScope?: unknown },
>(
  policies: T[],
): Omit<T, 'resourceType' | 'usesCommonScope'>[] =>
  policies.map(
    ({
      resourceType: _resourceType,
      usesCommonScope: _usesCommonScope,
      ...policy
    }) => policy,
  );

const getResourcePrefix = (value: string) => value.split(':')[0];

/**
 * The policy prefix for a resource type. Console enums are SCREAMING_CASE and
 * lowercase to the prefix; Backoffice enums are already the camelCase prefix.
 */
export const getResourceTypePrefix = (resourceType: string) =>
  resourceType === resourceType.toUpperCase()
    ? resourceType.toLowerCase()
    : resourceType;

export const supportsGroupAggregation = (resourceType: string): boolean =>
  GROUP_AGGREGATION_RESOURCE_TYPES.includes(resourceType as ResourceTypeEnum);

export const supportsTagAggregation = (resourceType: string): boolean =>
  TAG_AGGREGATION_RESOURCE_TYPES.includes(resourceType as ResourceTypeEnum);

export const isWildCardOnlyResourceType = (resourceType: string): boolean =>
  WILD_CARD_ONLY_RESOURCE_TYPES.includes(resourceType as ResourceTypeEnum);

/** The prefix a resource type's policy values are built from. */
const getPolicyPrefix = (resourceType: ResourceTypeEnum): string =>
  POLICY_PREFIX_OVERRIDES[resourceType] ?? getResourceTypePrefix(resourceType);

/** Resource value covering every resource of a type (`<prefix>:*`). */
export const getWildCardResource = (resourceType: ResourceTypeEnum): string =>
  `${getPolicyPrefix(resourceType)}:${WILD_CARD}`;

/**
 * Resource value that scopes a policy to everything in a group, mirroring the
 * value produced by the group aggregation selector.
 */
export const getGroupAggregationResource = (
  resourceType: ResourceTypeEnum,
  groupId: string,
): string => `${getPolicyPrefix(resourceType)}:group:${groupId}`;

/** Must match the value `PolicyTagSelect` writes. */
export const getTagAggregationResource = (
  resourceType: ResourceTypeEnum,
  tagName: string,
): string => `${getPolicyPrefix(resourceType)}:tag:${tagName}`;

/**
 * Whether a policy can be edited in the visual editor. The editor represents a
 * single resource type per policy, so a policy is editable only when all of its
 * actions and resources share one resource-type prefix. Policies created in the
 * new flow (with a `resourceType`) and still-empty policies are always editable;
 * legacy policies that mix resource types (still valid for the backend) are not.
 */
export const isPolicyEditable = (policy?: TPolicyInputSchema): boolean => {
  if (policy?.resourceType) {
    return true;
  }

  const values = [...(policy?.action ?? []), ...(policy?.resource ?? [])];

  if (!values.length) {
    return true;
  }

  if (values.some((value) => value === WILD_CARD)) {
    return false;
  }

  const prefixes = new Set(values.map(getResourcePrefix).filter(Boolean));

  return prefixes.size === 1;
};

/**
 * The resource type a policy targets: the one chosen when it was created, or
 * resolved from its action/resource for policies that predate that field.
 */
export const getPolicyResourceType = (
  policy: TPolicyInputSchema | undefined,
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
): ResourceTypeEnum =>
  policy?.resourceType ??
  getResourceTypeFromActions(
    configuredPolicies,
    defaultResourceType,
    policy?.action?.[0],
    policy?.resource?.[0],
  );

/**
 * Whether a policy of this resource type can take its resources from the common
 * scope. A wildcard-only type cannot: it is always `<prefix>:*` whatever the
 * scope says, so offering the choice would be a lie. Everything else needs the
 * matching aggregation, so a type without one (webhooks, or integrations under
 * a tag scope) keeps its own selector.
 */
export const canUseCommonScope = (
  resourceType: ResourceTypeEnum,
  mode: TCommonScopeMode,
  aggregations: TResourceAggregations,
): boolean => {
  if (mode === 'own' || isWildCardOnlyResourceType(resourceType)) {
    return false;
  }

  if (mode === 'all') {
    return true;
  }

  if (mode === 'groups') {
    return aggregations.group && supportsGroupAggregation(resourceType);
  }

  return aggregations.tags && supportsTagAggregation(resourceType);
};

/**
 * The resources a policy of this type takes from the common scope — empty when
 * it cannot use one, or when a group/tag scope has nothing selected yet.
 */
export const expandCommonScope = (
  commonScope: TCommonScope,
  resourceType: ResourceTypeEnum,
  aggregations: TResourceAggregations,
): string[] => {
  if (!canUseCommonScope(resourceType, commonScope.mode, aggregations)) {
    return [];
  }

  if (commonScope.mode === 'all') {
    return [getWildCardResource(resourceType)];
  }

  if (commonScope.mode === 'groups') {
    return commonScope.values.map((groupId) =>
      getGroupAggregationResource(resourceType, groupId),
    );
  }

  return commonScope.values.map((tagName) =>
    getTagAggregationResource(resourceType, tagName),
  );
};

const OWN_COMMON_SCOPE: TCommonScope = { mode: 'own', values: [] };

export const hasSameResources = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const getScopeKey = (scope: TCommonScope) =>
  `${scope.mode}:${[...scope.values].sort().join('|')}`;

/**
 * The scope the most policies already share, or undefined when the winner
 * covers fewer than half of the policies that could take it. A tie keeps the
 * first seen.
 */
const getMostCommonScope = (
  candidates: TCommonScope[],
  eligibleCount: number,
): TCommonScope | undefined => {
  const counts = new Map<string, { scope: TCommonScope; count: number }>();

  candidates.forEach((scope) => {
    const key = getScopeKey(scope);
    const entry = counts.get(key);

    if (entry) {
      entry.count += 1;
      return;
    }

    counts.set(key, { scope, count: 1 });
  });

  const best = [...counts.values()].reduce<
    { scope: TCommonScope; count: number } | undefined
  >(
    (best, entry) => (!best || entry.count > best.count ? entry : best),
    undefined,
  );

  return best && best.count * 2 >= eligibleCount ? best.scope : undefined;
};

// Reads the values back out of resources built by the matching builder, so a
// value only counts when it round-trips exactly.
const readAggregatedValues = (
  resources: string[],
  aggregationPrefix: string,
): string[] | null => {
  const values = resources.map((resource) =>
    resource.startsWith(aggregationPrefix)
      ? resource.slice(aggregationPrefix.length)
      : '',
  );

  return values.every(Boolean) ? values : null;
};

const classifyPolicyScope = (
  policy: TPolicyInputSchema,
  resourceType: ResourceTypeEnum,
): TCommonScope | null => {
  const resources = policy.resource ?? [];

  if (!resources.length) {
    return null;
  }

  if (
    resources.length === 1 &&
    resources[0] === getWildCardResource(resourceType)
  ) {
    return { mode: 'all', values: [] };
  }

  const groupIds = readAggregatedValues(
    resources,
    getGroupAggregationResource(resourceType, ''),
  );

  if (groupIds) {
    return { mode: 'groups', values: groupIds };
  }

  const tagNames = readAggregatedValues(
    resources,
    getTagAggregationResource(resourceType, ''),
  );

  if (tagNames) {
    return { mode: 'tags', values: tagNames };
  }

  return null;
};

/**
 * Rebuilds the common scope from saved policies, since it is never persisted.
 * Takes the scope the most policies already share rather than requiring every
 * one of them to agree: a role typically mixes a shared group with a policy or
 * two on their own resources, and demanding unanimity would report `own` for
 * almost every real role. Policies that do not match it read as overrides.
 * Wildcard-only types get no vote — their `<prefix>:*` is the same under every
 * scope, so it distinguishes nothing.
 *
 * The winner still has to cover at least half the policies that could take it.
 * A policy on its own resources abstains rather than voting, so without that
 * floor a single `<prefix>:*` among several id-scoped policies would report the
 * whole role as `all`.
 *
 * A mode the app does not support is never derived: Backoffice has no tags, so
 * a saved `<prefix>:tag:x` there reads as a per-policy resource rather than a
 * tag scope the editor could neither show nor rebuild.
 *
 * Never rewrites a resource: it only reports the common scope and which policies
 * currently match it.
 */
export const deriveCommonScope = (
  policies: TPolicyInputSchema[],
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
  aggregations: TResourceAggregations,
): { commonScope: TCommonScope; inherits: boolean[] } => {
  const resourceTypes = policies.map((policy) =>
    getPolicyResourceType(policy, configuredPolicies, defaultResourceType),
  );

  const eligible = policies
    .map((policy, index) => ({ policy, resourceType: resourceTypes[index] }))
    .filter(
      ({ policy, resourceType }) =>
        isPolicyEditable(policy) && !isWildCardOnlyResourceType(resourceType),
    );

  const candidates = eligible.flatMap(({ policy, resourceType }) => {
    const candidate = classifyPolicyScope(policy, resourceType);

    if (
      !candidate ||
      !canUseCommonScope(resourceType, candidate.mode, aggregations)
    ) {
      return [];
    }

    return [candidate];
  });

  const commonScope = getMostCommonScope(candidates, eligible.length);

  if (!commonScope) {
    return {
      commonScope: OWN_COMMON_SCOPE,
      inherits: policies.map(() => false),
    };
  }

  return {
    commonScope,
    inherits: policies.map((policy, index) => {
      if (!isPolicyEditable(policy)) {
        return false;
      }

      return hasSameResources(
        policy.resource ?? [],
        expandCommonScope(commonScope, resourceTypes[index], aggregations),
      );
    }),
  };
};

/**
 * Splits a legacy policy into one policy per resource type: actions are grouped
 * by their resource-type prefix and each group keeps the resources that share
 * that prefix. A bare `*` resource ("all resources") is scoped to each split as
 * `<prefix>:*`, preserving the intent while keeping the policy valid. Bare `*`
 * actions are dropped, and groups left with no resource of their own (an action
 * prefix the original resources never matched) are skipped rather than emitted
 * as invalid resourceless policies.
 */
export const convertPolicyToNewFormat = (
  policy: TPolicyInputSchema,
  configuredPolicies: TPolicy,
  defaultResourceType: ResourceTypeEnum,
): TPolicyInputSchema[] => {
  const actionsByPrefix = (policy.action ?? []).reduce<
    Record<string, string[]>
  >((groups, action) => {
    const prefix = getResourcePrefix(action);

    if (!prefix || prefix === WILD_CARD) {
      return groups;
    }

    groups[prefix] = [...(groups[prefix] ?? []), action];
    return groups;
  }, {});

  const hasWildCardResource = (policy.resource ?? []).includes(WILD_CARD);

  return Object.entries(actionsByPrefix).flatMap(([prefix, actions]) => {
    const resource = (policy.resource ?? []).filter(
      (value) => getResourcePrefix(value) === prefix,
    );

    const wildCardResource = `${prefix}:${WILD_CARD}`;

    if (hasWildCardResource && !resource.includes(wildCardResource)) {
      resource.push(wildCardResource);
    }

    if (!resource.length) {
      return [];
    }

    return [
      {
        name: policy.name,
        description: policy.description,
        action: actions,
        resource,
        resourceType: getResourceTypeFromActions(
          configuredPolicies,
          defaultResourceType,
          actions[0],
          resource[0],
        ),
      },
    ];
  });
};

/** Whether converting a legacy policy omitted at least one original action. */
export const hasDroppedActionsDuringConversion = (
  policy: TPolicyInputSchema,
  convertedPolicies: TPolicyInputSchema[],
): boolean => {
  const convertedActions = new Set(
    convertedPolicies.flatMap((convertedPolicy) => convertedPolicy.action),
  );

  return (policy.action ?? []).some((action) => !convertedActions.has(action));
};

export const getObjectIndexAtPosition = (
  jsonContent: string,
  line: number,
  column: number,
): number | null => {
  try {
    const parsedContent = JSON.parse(jsonContent);

    if (!Array.isArray(parsedContent)) {
      console.log('Content is not an array');
      return null;
    }

    // Convert line/column to character offset
    const lines = jsonContent.split('\n');
    let charOffset = 0;

    // Calculate character offset up to the clicked line
    for (let i = 0; i < Math.min(line - 1, lines.length); i++) {
      charOffset += lines[i].length + 1; // +1 for newline character
    }
    // Add column offset within the current line
    charOffset += Math.min(column - 1, lines[line - 1]?.length || 0);

    // Parse through JSON to find object boundaries
    let currentObjectIndex = -1;
    let inMainArray = false;
    let braceDepth = 0;
    let bracketDepth = 0;
    let inString = false;
    let escapeNext = false;
    let objectStartPositions: number[] = [];
    let objectEndPositions: number[] = [];

    for (let i = 0; i < jsonContent.length; i++) {
      const char = jsonContent[i];

      // Handle string escaping
      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        escapeNext = true;
        continue;
      }

      // Handle string boundaries
      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }

      // Skip characters inside strings
      if (inString) continue;

      // Handle array brackets
      if (char === '[') {
        bracketDepth++;
        if (bracketDepth === 1) {
          inMainArray = true;
        }
        continue;
      }

      if (char === ']') {
        bracketDepth--;
        if (bracketDepth === 0) {
          inMainArray = false;
        }
        continue;
      }

      // Only process if we're in the main array
      if (!inMainArray) continue;

      // Handle object braces
      if (char === '{') {
        if (braceDepth === 0) {
          // Starting a new top-level object
          currentObjectIndex++;
          objectStartPositions[currentObjectIndex] = i;
        }
        braceDepth++;
      }

      if (char === '}') {
        braceDepth--;
        if (braceDepth === 0) {
          // Ending a top-level object
          objectEndPositions[currentObjectIndex] = i;
        }
      }
    }

    // Find which object contains the clicked position
    for (let i = 0; i < objectStartPositions.length; i++) {
      const startPos = objectStartPositions[i];
      const endPos = objectEndPositions[i];

      if (startPos !== undefined && endPos !== undefined) {
        if (charOffset >= startPos && charOffset <= endPos) {
          return i;
        }
      }
    }

    // If we didn't find an exact match, find the closest object
    // This handles cases where the user clicks on whitespace or commas between objects
    let closestObjectIndex = -1;
    let minDistance = Infinity;

    for (let i = 0; i < objectStartPositions.length; i++) {
      const startPos = objectStartPositions[i];
      const endPos = objectEndPositions[i];

      if (startPos !== undefined && endPos !== undefined) {
        let distance;

        if (charOffset < startPos) {
          distance = startPos - charOffset;
        } else if (charOffset > endPos) {
          distance = charOffset - endPos;
        } else {
          // This shouldn't happen as we already checked exact matches above
          distance = 0;
        }

        if (distance < minDistance) {
          minDistance = distance;
          closestObjectIndex = i;
        }
      }
    }

    // Only return closest match if it's reasonably close (within same line or adjacent lines)
    if (closestObjectIndex !== -1 && minDistance < 100) {
      return closestObjectIndex;
    }

    return null;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return null;
  }
};
